import { DatePipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { ConsultantHabilitationModel } from '@lib/models/consultant/consultant.model';
import { ConsultantService } from '@lib/services/consultant/consultant.service';
import { OuiAccordionModule } from 'omnium-ui/accordion';
import { OuiDialogService } from 'omnium-ui/dialog';
import { OuiIconModule } from 'omnium-ui/icon';
import { OuiPairInfo } from 'omnium-ui/pair-info';
import { OuiSlideToggle } from 'omnium-ui/slide-toggle';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTagModule } from 'omnium-ui/tag';
import { firstValueFrom } from 'rxjs';
import {
  HabilitationActivationDialogComponent,
  HabilitationActivationDialogData,
} from '../../../dialogs/habilitation-activation-dialog/habilitation-activation-dialog.component';
import {
  HabilitationDesactivationDialogComponent,
  HabilitationDesactivationDialogData,
} from '../../../dialogs/habilitation-desactivation-dialog/habilitation-desactivation-dialog.component';
import { DemandeHabilitation } from '@lib/models/demande-habilitation/demande-habilitation.model';
import { getHabilitationCurrentStepPresentation } from '@lib/models/demande-habilitation/habilitation-parcours-presentation';
import { OuiButton } from 'omnium-ui/button';

@Component({
  selector: 'lib-habilitation',
  standalone: true,
  imports: [
    OuiAccordionModule,
    OuiTagModule,
    OuiIconModule,
    OuiPairInfo,
    OuiSlideToggle,
    OuiButton,
    DatePipe,
  ],
  templateUrl: './habilitation.component.html',
  styleUrl: './habilitation.component.scss',
  host: {
    class: 'oui-elevation-04',
  },
})
export class HabilitationComponent {
  private readonly consultantService = inject(ConsultantService);
  private readonly dialogService = inject(OuiDialogService);
  private readonly snackbarService = inject(OuiSnackbarService);

  readonly codeConsultant = input.required<string>();
  readonly libelle = input.required<string>();
  readonly isBo = input<boolean>(false);
  readonly canInitiateParcours = input<boolean>(false);
  readonly habilitation = input.required<ConsultantHabilitationModel | undefined>();
  readonly demandeHabilitation = input<DemandeHabilitation | undefined>();
  readonly commissionAdi = input<string | undefined>();
  readonly refreshRequested = output<void>();
  readonly initiateParcours = output<void>();

  readonly currentStepStatus = computed(() => {
    const demande = this.demandeHabilitation();

    return demande
      ? getHabilitationCurrentStepPresentation(
          this.isBo() ? 'shm' : 'consultant',
          demande.etapeCourante,
          demande.statut
        )
      : undefined;
  });

  readonly isSaving = signal<boolean>(false);
  readonly isConfirmationOpen = signal<boolean>(false);
  readonly activeControl = new FormControl<boolean | null>(false);

  constructor() {
    effect(() => {
      this.resetActiveControl();
    });
  }

  async onActiveToggleClick(event: MouseEvent) {
    event.stopPropagation();

    const hab = this.habilitation();

    if (!hab) return;

    if (this.isActiveForcedByOverride(hab)) {
      this.resetActiveControl(hab);
      return;
    }

    if (this.isSaving() || this.isConfirmationOpen()) {
      this.resetActiveControl(hab);
      return;
    }

    if (!hab.actif) {
      this.resetActiveControl(hab);
      if (this.isInactiveForcedByOverride(hab)) {
        await this.confirmAndCancelDeactivation(hab);
        return;
      }

      await this.confirmAndActivate(hab);
      return;
    }

    this.resetActiveControl(hab);
    await this.confirmAndDeactivate(hab);
  }

  private async confirmAndActivate(hab: ConsultantHabilitationModel) {
    this.resetActiveControl(hab);

    const motif = await this.getActivationMotif('temporaryActivation');

    if (typeof motif !== 'string') {
      this.resetActiveControl(hab);
      return;
    }

    try {
      this.isSaving.set(true);

      await this.consultantService.setHabilitationOverride(
        this.codeConsultant(),
        hab.id,
        hab.code,
        true,
        motif
      );

      this.snackbarService.open('Habilitation activée avec succès !', 'success');
      this.activeControl.setValue(true, { emitEvent: false });
      this.refreshRequested.emit();
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue pendant l'activation de l'habilitation",
        'error'
      );
      this.resetActiveControl(hab);
    } finally {
      this.isSaving.set(false);
    }
  }

  private async confirmAndCancelDeactivation(hab: ConsultantHabilitationModel) {
    this.resetActiveControl(hab);

    const motif = await this.getActivationMotif('reactivation');

    if (typeof motif !== 'string') {
      this.resetActiveControl(hab);
      return;
    }

    try {
      this.isSaving.set(true);

      await this.consultantService.cancelHabilitationOverride(
        this.codeConsultant(),
        hab.id,
        hab.code,
        motif
      );

      this.snackbarService.open('Habilitation activée avec succès !', 'success');
      this.activeControl.setValue(true, { emitEvent: false });
      this.refreshRequested.emit();
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue pendant l'activation de l'habilitation",
        'error'
      );
      this.resetActiveControl(hab);
    } finally {
      this.isSaving.set(false);
    }
  }

  private async getActivationMotif(
    mode: HabilitationActivationDialogData['mode']
  ): Promise<string | undefined> {
    this.isConfirmationOpen.set(true);
    try {
      const motif = await firstValueFrom(
        this.dialogService
          .openDialog(HabilitationActivationDialogComponent, {
            habilitationName: this.libelle(),
            mode,
          } as HabilitationActivationDialogData)
          .afterClosed()
      );

      if (typeof motif !== 'string' || motif.trim().length === 0) {
        return undefined;
      }

      return motif.trim();
    } finally {
      this.isConfirmationOpen.set(false);
    }
  }

  private async confirmAndDeactivate(hab: ConsultantHabilitationModel) {
    this.resetActiveControl(hab);

    this.isConfirmationOpen.set(true);
    const confirmed = await firstValueFrom(
      this.dialogService
        .openDialog(HabilitationDesactivationDialogComponent, {
          habilitationName: this.libelle(),
        } as HabilitationDesactivationDialogData)
        .afterClosed()
    );
    this.isConfirmationOpen.set(false);

    if (confirmed !== true) {
      this.resetActiveControl(hab);
      return;
    }

    try {
      this.isSaving.set(true);

      await this.consultantService.setHabilitationOverride(
        this.codeConsultant(),
        hab.id,
        hab.code,
        false,
        ''
      );

      this.snackbarService.open(
        "La désactivation de l'habilitation a bien été enregistrée",
        'success'
      );
      this.activeControl.setValue(false, { emitEvent: false });
      this.refreshRequested.emit();
    } catch (error) {
      console.error(error);
      this.snackbarService.open(
        "Une erreur est survenue pendant la désactivation de l'habilitation",
        'error'
      );
      this.resetActiveControl(hab);
    } finally {
      this.isSaving.set(false);
    }
  }

  private resetActiveControl(hab = this.habilitation()) {
    this.activeControl.setValue(hab?.actif ?? false, { emitEvent: false });

    if (this.isActiveForcedByOverride(hab)) {
      this.activeControl.disable({ emitEvent: false });
      return;
    }

    this.activeControl.enable({ emitEvent: false });
  }

  private isActiveForcedByOverride(
    hab: ConsultantHabilitationModel | undefined
  ): boolean {
    return hab?.override?.consultantHabilitationTempStatut === true;
  }

  private isInactiveForcedByOverride(
    hab: ConsultantHabilitationModel | undefined
  ): boolean {
    return hab?.override?.consultantHabilitationTempStatut === false;
  }
}
